import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Alert, Button, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShield,
  faChartBar,
  faGlobe,
  faBullseye,
  faBolt,
  faKey,
  faFolder,
  faBroadcastTower,
  faTerminal,
  faBox,
  faCircle,
  faImage,
  faClock,
  faNetworkWired,
  faServer,
  faProjectDiagram,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import BeaconManager from '../components/c2/BeaconManager';
import TaskManager from '../components/c2/TaskManager';
import CredentialVault from '../components/c2/CredentialVault';
import C2Terminal from '../components/c2/C2Terminal';
import ListenerManager from '../components/c2/ListenerManager';
import FileManager from '../components/c2/FileManager';
import ScreenshotGallery from '../components/c2/ScreenshotGallery';
import JobsList from '../components/c2/JobsList';
import PortScanner from '../components/c2/PortScanner';
import ShareBrowser from '../components/c2/ShareBrowser';
import EnhancedNetworkGraph from '../components/c2/EnhancedNetworkGraph';
import MetricsDashboard from '../components/c2/MetricsDashboard';
import StagerBuilder from '../components/c2/StagerBuilder';
import RedirectorManager from '../components/c2/RedirectorManager';
import ServerRegistry from '../components/c2/ServerRegistry';
import InfrastructureBuilder from '../components/c2/infrastructure/InfrastructureBuilder';
import C2Help from '../components/c2/C2Help';
import c2Service from '../services/c2Service';
import projectService from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';

const C2Console = () => {
  const { projectId } = useParams();
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('c2_activeTab') || 'overview');

  // Persist active tab
  useEffect(() => { localStorage.setItem('c2_activeTab', activeTab); }, [activeTab]);
  const [project, setProject] = useState(null);
  const [beacons, setBeacons] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [redirectors, setRedirectors] = useState([]);
  const [selectedBeacon, setSelectedBeacon] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNetworkViz, setShowNetworkViz] = useState(false);
  const [taskHistory, setTaskHistory] = useState([]);
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const { user } = useAuth();

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  useEffect(() => {
    loadBeacons();
    loadListeners();
    loadRedirectors();
    loadTaskHistory();

    // Periodic beacon refresh — keeps isAlive/lastSeen current for
    // Network Topology, Beacon Manager, and File Browser status dots.
    // Check-ins update lastSeen in DB but don't broadcast via WebSocket,
    // so without polling the frontend shows stale alive/dead status.
    const beaconRefreshInterval = setInterval(() => {
      loadBeacons();
    }, 15000); // 15 seconds

    // Connect WebSocket for admins or users with c2.console.access permission
    if (user && (user.role === 'admin' || (user.permissions && user.permissions.includes('c2.console.access')))) {
      connectWebSocket();
    }

    return () => {
      clearInterval(beaconRefreshInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, projectId]);

  const connectWebSocket = () => {
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/c2console`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('[WebSocket] Connected, sending auth...');

      // Send authentication message with userId and tenantId
      if (user) {
        wsRef.current.send(JSON.stringify({
          type: 'auth',
          userId: user._id || user.id,
          tenantId: user.tenant || user.tenantId
        }));
      } else {
        console.error('[WebSocket] No user available for auth');
        addNotification('Authentication failed - no user', 'danger');
        wsRef.current.close();
      }
    };

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    };

    wsRef.current.onclose = (event) => {
      setWsConnected(false);
      console.log('[WebSocket] Closed:', event.code, event.reason);

      reconnectAttemptsRef.current++;

      // Only show notification on first few failures to avoid spam
      if (reconnectAttemptsRef.current <= 3) {
        addNotification(`Disconnected from C2 console (code: ${event.code})`, 'warning');
      }

      // Exponential backoff: 5s, 10s, 20s, max 60s
      const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current - 1), 60000);
      console.log(`[WebSocket] Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current})`);
      setTimeout(connectWebSocket, delay);
    };

    wsRef.current.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      addNotification('WebSocket connection error', 'danger');
    };
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'auth_success':
        console.log('[C2 Console] Authenticated:', message.message);
        setWsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnect counter on success
        addNotification('Connected to C2 console', 'success');
        break;
      case 'auth_error':
        console.error('[C2 Console] Auth error:', message.error);
        addNotification(`Authentication failed: ${message.error}`, 'danger');
        setWsConnected(false);
        break;
      case 'pong':
        // Heartbeat response
        break;
      case 'beacon_checkin':
        addNotification(`New beacon: ${message.beacon.beaconId} (${message.beacon.systemInfo.hostname})`, 'info');
        loadBeacons();
        break;
      case 'beacon_update':
        updateBeaconInList(message.beacon);
        break;
      case 'task_created':
        addNotification(`Task created: ${message.task.command} on ${message.beaconId}`, 'info');
        loadTaskHistory();
        break;
      case 'task_completed':
        addNotification(`Task completed: ${message.task.command} on ${message.beaconId}`, 'success');
        loadTaskHistory();
        break;
      case 'beacon_task_update':
        loadTaskHistory();
        break;
      default:
        console.log('Unknown WebSocket message:', message);
    }
  };

  const loadProject = async () => {
    try {
      const response = await projectService.getProject(projectId);
      // Axios wraps response, actual data is at response.data
      setProject(response.data?.data || response.data);
    } catch (error) {
      console.error('Failed to load project:', error);
      addNotification('Failed to load project', 'danger');
    }
  };

  const loadBeacons = async () => {
    try {
      const filters = projectId ? { project: projectId } : {};
      const response = await c2Service.getBeacons(filters);
      // Backend returns {success, count, data: [...beacons]}
      // Axios wraps it, so actual beacons are at response.data.data
      const beaconData = response.data?.data || [];
      setBeacons(Array.isArray(beaconData) ? beaconData : []);
    } catch (error) {
      console.error('Failed to load beacons:', error);
      addNotification('Failed to load beacons', 'danger');
      setBeacons([]);
    }
  };

  const loadListeners = async () => {
    try {
      const response = await c2Service.getListeners();
      // Backend returns {success, data: [...listeners]}
      // Axios wraps it, so actual listeners are at response.data.data
      const listenerData = response.data?.data || [];
      setListeners(Array.isArray(listenerData) ? listenerData : []);
    } catch (error) {
      console.error('Failed to load listeners:', error);
      setListeners([]);
    }
  };

  const loadRedirectors = async () => {
    try {
      const response = await c2Service.getRedirectors();
      const redirectorData = response.data?.data || [];
      setRedirectors(Array.isArray(redirectorData) ? redirectorData : []);
    } catch (error) {
      console.error('Failed to load redirectors:', error);
      setRedirectors([]);
    }
  };

  const loadTaskHistory = async () => {
    try {
      // TEMP: Remove project filter to show all tasks
      const filters = {};
      const response = await c2Service.getAllTasks(filters);
      // Backend returns {success, data: [...tasks]}
      // Axios wraps it, so actual tasks are at response.data.data
      const taskData = response.data?.data || [];
      console.log('[DEBUG] Loaded tasks:', taskData.length, taskData);
      setTaskHistory(Array.isArray(taskData) ? taskData : []);
    } catch (error) {
      console.error('Failed to load task history:', error);
      setTaskHistory([]);
    }
  };

  const updateBeaconInList = (updatedBeacon) => {
    setBeacons(prev => {
      const beaconArray = Array.isArray(prev) ? prev : [];
      return beaconArray.map(b =>
        b.beaconId === updatedBeacon.beaconId ? { ...b, ...updatedBeacon } : b
      );
    });
  };

  const addNotification = (message, variant) => {
    // Prevent duplicate notifications (especially for repeated WebSocket errors)
    setNotifications(prev => {
      const notifArray = Array.isArray(prev) ? prev : [];
      const isDuplicate = notifArray.some(n =>
        n.message === message && (Date.now() - n.id) < 10000 // Same message within 10 seconds
      );
      if (isDuplicate) return notifArray;

      const notification = {
        id: Date.now(),
        message,
        variant,
        timestamp: new Date()
      };

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setNotifications(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter(n => n.id !== notification.id);
        });
      }, 5000);

      return [notification, ...notifArray].slice(0, 3); // Keep max 3
    });
  };

  const safeBeacons = Array.isArray(beacons) ? beacons : [];
  const safeListeners = Array.isArray(listeners) ? listeners : [];
  const safeTaskHistory = Array.isArray(taskHistory) ? taskHistory : [];
  const safeNotifications = Array.isArray(notifications) ? notifications : [];

  const aliveBeacons = safeBeacons.filter(b => b.isAlive);
  const stats = {
    totalBeacons: safeBeacons.length,
    aliveBeacons: aliveBeacons.length,
    pendingBeacons: safeBeacons.filter(b => !b.isApproved).length,
    totalTasks: safeTaskHistory.length
  };

  return (
    <>
      {/* Toast notification animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

    <Container fluid className="py-4">
      {/* FORCED Horizontal Header with inline styles */}
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #374151', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', fontSize: '1.25rem', fontWeight: '600', color: '#fff' }}>
            <FontAwesomeIcon icon={faShield} style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
            C2 Console
          </h4>
          {project && (
            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              {project.name} ({project.client})
            </span>
          )}
          <span style={{
            backgroundColor: wsConnected ? '#10b981' : '#ef4444',
            color: '#fff',
            padding: '0.25rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: '0.5rem' }} />
            {wsConnected ? 'Connected' : 'Disconnected'}
          </span>
          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
            {safeBeacons.length} beacons • {aliveBeacons.length} alive
          </span>
        </div>
        <button
          onClick={() => {
            console.log('Network View button clicked');
            setShowNetworkViz(true);
          }}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #3b82f6',
            color: '#3b82f6',
            padding: '0.375rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 1000
          }}
        >
          <FontAwesomeIcon icon={faGlobe} />
          Network View
        </button>
      </div>

      {/* Toast Notifications - Fixed position overlay in upper right */}
      {safeNotifications.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 9999,
          maxWidth: '350px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {safeNotifications.slice(0, 3).map(notif => (
            <div
              key={notif.id}
              style={{
                backgroundColor: notif.variant === 'success' ? '#10b981' :
                                 notif.variant === 'warning' ? '#f59e0b' :
                                 notif.variant === 'danger' ? '#ef4444' : '#3b82f6',
                color: '#fff',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                fontSize: '0.875rem',
                animation: 'slideInRight 0.3s ease-out',
                minWidth: '250px'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>
                  {notif.timestamp.toLocaleTimeString()}
                </div>
                <div>{notif.message}</div>
              </div>
              <button
                onClick={() => setNotifications(prev => {
                  const prevArray = Array.isArray(prev) ? prev : [];
                  return prevArray.filter(n => n.id !== notif.id);
                })}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0',
                  lineHeight: '1',
                  opacity: 0.7,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* HORIZONTAL TABS NAVIGATION - Compact Web Testing Style */}
      <div className="mb-3" style={{ borderBottom: '1px solid #2d3748' }}>
        <div className="flex flex-wrap gap-1" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingBottom: '0' }}>
          <button
            onClick={() => setActiveTab('overview')}
            className={`transition-colors text-sm ${
              activeTab === 'overview'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'overview' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'overview' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'overview' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '12px', marginRight: '6px' }} />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('beacons')}
            className={`transition-colors text-sm ${
              activeTab === 'beacons'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'beacons' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'beacons' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'beacons' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faBullseye} style={{ fontSize: '12px', marginRight: '6px' }} />
            Beacons
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`transition-colors text-sm ${
              activeTab === 'tasks'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'tasks' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'tasks' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'tasks' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faBolt} style={{ fontSize: '12px', marginRight: '6px' }} />
            Tasks
          </button>
          <button
            onClick={() => setActiveTab('credentials')}
            className={`transition-colors text-sm ${
              activeTab === 'credentials'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'credentials' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'credentials' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'credentials' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faKey} style={{ fontSize: '12px', marginRight: '6px' }} />
            Credentials
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`transition-colors text-sm ${
              activeTab === 'files'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'files' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'files' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'files' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faFolder} style={{ fontSize: '12px', marginRight: '6px' }} />
            Files
          </button>
          <button
            onClick={() => setActiveTab('screenshots')}
            className={`transition-colors text-sm ${
              activeTab === 'screenshots'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'screenshots' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'screenshots' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'screenshots' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faImage} style={{ fontSize: '12px', marginRight: '6px' }} />
            Screenshots
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`transition-colors text-sm ${
              activeTab === 'jobs'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'jobs' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'jobs' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'jobs' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faClock} style={{ fontSize: '12px', marginRight: '6px' }} />
            Jobs
          </button>
          <button
            onClick={() => setActiveTab('portscanner')}
            className={`transition-colors text-sm ${
              activeTab === 'portscanner'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'portscanner' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'portscanner' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'portscanner' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faNetworkWired} style={{ fontSize: '12px', marginRight: '6px' }} />
            Port Scanner
          </button>
          <button
            onClick={() => setActiveTab('shares')}
            className={`transition-colors text-sm ${
              activeTab === 'shares'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'shares' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'shares' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'shares' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faFolder} style={{ fontSize: '12px', marginRight: '6px' }} />
            File Browser
          </button>
          <button
            onClick={() => setActiveTab('listeners')}
            className={`transition-colors text-sm ${
              activeTab === 'listeners'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'listeners' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'listeners' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'listeners' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faBroadcastTower} style={{ fontSize: '12px', marginRight: '6px' }} />
            Listeners
          </button>
          <button
            onClick={() => setActiveTab('terminal')}
            className={`transition-colors text-sm ${
              activeTab === 'terminal'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'terminal' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'terminal' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'terminal' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faTerminal} style={{ fontSize: '12px', marginRight: '6px' }} />
            Terminal
          </button>
          <button
            onClick={() => setActiveTab('payloads')}
            className={`transition-colors text-sm ${
              activeTab === 'payloads'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'payloads' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'payloads' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'payloads' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faBox} style={{ fontSize: '12px', marginRight: '6px' }} />
            Payloads
          </button>
          <button
            onClick={() => setActiveTab('redirectors')}
            className={`transition-colors text-sm ${
              activeTab === 'redirectors'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'redirectors' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'redirectors' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'redirectors' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faProjectDiagram} style={{ fontSize: '12px', marginRight: '6px' }} />
            Redirectors
          </button>
          <button
            onClick={() => setActiveTab('infrastructure')}
            className={`transition-colors text-sm ${
              activeTab === 'infrastructure'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'infrastructure' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'infrastructure' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'infrastructure' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faProjectDiagram} style={{ fontSize: '12px', marginRight: '6px' }} />
            Infrastructure
          </button>
          <button
            onClick={() => setActiveTab('servers')}
            className={`transition-colors text-sm ${
              activeTab === 'servers'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'servers' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'servers' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'servers' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faServer} style={{ fontSize: '12px', marginRight: '6px' }} />
            Servers
          </button>
          <button
            onClick={() => setActiveTab('help')}
            className={`transition-colors text-sm ${
              activeTab === 'help'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={{
              padding: '6px 12px',
              background: activeTab === 'help' ? 'rgba(66, 153, 225, 0.15)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'help' ? '2px solid #4299e1' : '2px solid transparent',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: activeTab === 'help' ? '500' : '400'
            }}
          >
            <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: '12px', marginRight: '6px' }} />
            Help
          </button>
        </div>
      </div>

      {/* TAB CONTENT */}
      <Row>
        <Col>
          {activeTab === 'overview' && (
            <div>
                {/* FORCED Horizontal Stats Cards - Using inline styles to override CSS */}
                <div className="mb-4" style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '200px', backgroundColor: '#1f2937', border: '1px solid #3b82f6', borderRadius: '0.5rem', padding: '1.5rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h2 style={{ color: '#3b82f6', marginBottom: '0.25rem', fontSize: '2rem' }}>{aliveBeacons.length}</h2>
                      <small style={{ color: '#9ca3af' }}>Live Beacons</small>
                    </div>
                  </div>
                  <div style={{ flex: '1', minWidth: '200px', backgroundColor: '#1f2937', border: '1px solid #f59e0b', borderRadius: '0.5rem', padding: '1.5rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h2 style={{ color: '#f59e0b', marginBottom: '0.25rem', fontSize: '2rem' }}>{stats.pendingBeacons}</h2>
                      <small style={{ color: '#9ca3af' }}>Pending Approval</small>
                    </div>
                  </div>
                  <div style={{ flex: '1', minWidth: '200px', backgroundColor: '#1f2937', border: '1px solid #06b6d4', borderRadius: '0.5rem', padding: '1.5rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h2 style={{ color: '#06b6d4', marginBottom: '0.25rem', fontSize: '2rem' }}>{stats.totalTasks}</h2>
                      <small style={{ color: '#9ca3af' }}>Total Tasks</small>
                    </div>
                  </div>
                  <div style={{ flex: '1', minWidth: '200px', backgroundColor: '#1f2937', border: '1px solid #6b7280', borderRadius: '0.5rem', padding: '1.5rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <h2 style={{ color: '#6b7280', marginBottom: '0.25rem', fontSize: '2rem' }}>{stats.totalBeacons - aliveBeacons.length}</h2>
                      <small style={{ color: '#9ca3af' }}>Inactive</small>
                    </div>
                  </div>
                </div>
                {/* FORCED Horizontal Layout for Recent Beacons and Activity */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
                  <div style={{ flex: '1', minWidth: '400px', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: '#374151', padding: '0.75rem 1rem', borderBottom: '1px solid #4b5563' }}>
                      <strong style={{ color: '#fff' }}>
                        <FontAwesomeIcon icon={faBullseye} className="me-2" style={{ color: '#3b82f6' }} />
                        Recent Beacons
                      </strong>
                    </div>
                    <div style={{ padding: '1rem' }}>
                        {safeBeacons.slice(0, 5).map(beacon => (
                          <div key={beacon._id} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                            <div>
                              <strong>{beacon.beaconId}</strong>
                              <br />
                              <small className="text-muted">
                                {beacon.systemInfo?.hostname} • {beacon.systemInfo?.os}
                              </small>
                            </div>
                            <Badge bg={beacon.isAlive ? 'success' : 'secondary'}>
                              {beacon.isAlive ? 'Alive' : 'Offline'}
                            </Badge>
                          </div>
                        ))}
                        {safeBeacons.length === 0 && (
                          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem 0' }}>
                            <FontAwesomeIcon icon={faBullseye} size="3x" style={{ marginBottom: '1rem', opacity: 0.25 }} />
                            <p style={{ marginBottom: '0' }}>No beacons yet</p>
                            <small>Beacons will appear here when they connect</small>
                          </div>
                        )}
                    </div>
                  </div>
                  <div style={{ flex: '1', minWidth: '400px', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: '#374151', padding: '0.75rem 1rem', borderBottom: '1px solid #4b5563' }}>
                      <strong style={{ color: '#fff' }}>
                        <FontAwesomeIcon icon={faBolt} className="me-2" style={{ color: '#f59e0b' }} />
                        Activity Log
                      </strong>
                    </div>
                    <div style={{ padding: '1rem' }}>
                        {safeNotifications.slice(0, 8).map(notif => (
                          <div key={notif.id} className="mb-2 pb-2 border-bottom">
                            <small className="text-muted">{notif.timestamp.toLocaleTimeString()}</small>
                            <br />
                            <small>{notif.message}</small>
                          </div>
                        ))}
                        {safeNotifications.length === 0 && (
                          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem 0' }}>
                            <FontAwesomeIcon icon={faBolt} size="3x" style={{ marginBottom: '1rem', opacity: 0.25 }} />
                            <p style={{ marginBottom: '0' }}>No recent activity</p>
                            <small>Activity will be logged here</small>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
            </div>
          )}

          {activeTab === 'beacons' && (
            <div>
                <BeaconManager
                  beacons={safeBeacons}
                  selectedBeacon={selectedBeacon}
                  onSelectBeacon={setSelectedBeacon}
                  onRefresh={loadBeacons}
                  wsConnected={wsConnected}
                  projectId={projectId}
                  project={project}
                />
            </div>
          )}

          {activeTab === 'tasks' && (
            <TaskManager
              beacons={safeBeacons}
              selectedBeacon={selectedBeacon}
              onRefresh={loadBeacons}
              wsRef={wsRef}
            />
          )}

          {activeTab === 'credentials' && (
            <CredentialVault />
          )}

          {activeTab === 'files' && (
            <FileManager beacons={safeBeacons} />
          )}

          {activeTab === 'screenshots' && (
            <ScreenshotGallery beacons={safeBeacons} />
          )}

          {activeTab === 'jobs' && (
            <JobsList wsRef={wsRef} />
          )}

          {activeTab === 'portscanner' && (
            <PortScanner />
          )}

          {activeTab === 'shares' && (
            <ShareBrowser beacons={safeBeacons} projectId={projectId} />
          )}

          {activeTab === 'listeners' && (
            <ListenerManager />
          )}

          {activeTab === 'terminal' && (
            <C2Terminal
              beacons={safeBeacons}
              selectedBeacon={selectedBeacon}
              onSelectBeacon={setSelectedBeacon}
              wsRef={wsRef}
            />
          )}

          {activeTab === 'payloads' && (
            <StagerBuilder />
          )}

          {activeTab === 'redirectors' && (
            <RedirectorManager projectId={projectId} />
          )}

          {activeTab === 'infrastructure' && (
            <InfrastructureBuilder projectId={projectId} />
          )}

          {activeTab === 'servers' && (
            <ServerRegistry />
          )}

          {activeTab === 'help' && (
            <C2Help />
          )}
        </Col>
      </Row>

      {/* Network Visualization */}
      <EnhancedNetworkGraph
        show={showNetworkViz}
        onHide={() => setShowNetworkViz(false)}
        beacons={safeBeacons}
        listeners={safeListeners}
        redirectors={Array.isArray(redirectors) ? redirectors : []}
        onNodeClick={(node) => {
          if (node.type === 'beacon') {
            const beacon = safeBeacons.find(b => b.beaconId === node.id);
            if (beacon) {
              setSelectedBeacon(beacon);
            }
          }
          setShowNetworkViz(false);
        }}
      />
    </Container>
    </>
  );
};

export default C2Console;
